'use client'

/**
 * batch-68 handtekening (FO-345 §6) - een tekenvak met de keuze "Gebruik mijn
 * opgeslagen handtekening". Drop-in voor `SignaturePad` bij flows met een EIGEN
 * opslagveld (gereedschapsuitgifte-ontvangst, de eigen handtekening op het
 * aanrijdingsformulier, de handtekening in een controlelijst): de opslag van die
 * flows verandert niet, alleen de UI biedt de keuze. Gekozen = de app haalt een
 * verse voorbeeld-URL (kortlevend, eigenaar) op en zet die om naar een PNG-data-
 * URL; `toDataURL()` geeft dan die afbeelding terug, precies zoals een getekende.
 *
 * Nooit automatisch: de opgeslagen handtekening wordt pas gebruikt na een
 * bewuste klik.
 */
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { SignaturePad, type SignaturePadHandle, type SignaturePadProps } from '../signature-pad'

export interface SignaturePadMetOpgeslagenProps extends SignaturePadProps {
  /** Is er een opgeslagen handtekening? Zo nee: gewoon het tekenvak. */
  heeftOpgeslagen: boolean
  /** Haal op het moment van kiezen een VERSE voorbeeld-URL op (null = niet beschikbaar). */
  haalVoorbeeldUrl: () => Promise<string | null>
}

async function urlNaarDataUrl(url: string): Promise<string> {
  const res = await fetch(url, { cache: 'no-store', credentials: 'same-origin' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const blob = await res.blob()
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('lezen mislukt'))
    reader.readAsDataURL(blob)
  })
}

export const SignaturePadMetOpgeslagen = forwardRef<SignaturePadHandle, SignaturePadMetOpgeslagenProps>(
  function SignaturePadMetOpgeslagen({ heeftOpgeslagen, haalVoorbeeldUrl, ...padProps }, ref) {
    const padRef = useRef<SignaturePadHandle>(null)
    const [opgeslagen, setOpgeslagen] = useState<string | null>(null)
    const [bezig, setBezig] = useState(false)
    const [fout, setFout] = useState<string | null>(null)

    useImperativeHandle(
      ref,
      (): SignaturePadHandle => ({
        clear: () => {
          setOpgeslagen(null)
          padRef.current?.clear()
        },
        toDataURL: () => opgeslagen ?? padRef.current?.toDataURL() ?? '',
        isEmpty: () => (opgeslagen ? false : padRef.current?.isEmpty() ?? true),
        undo: () => padRef.current?.undo?.(),
        aantalStreken: () => (opgeslagen ? 2 : padRef.current?.aantalStreken?.() ?? 0),
        inktBreedte: () => (opgeslagen ? 1000 : padRef.current?.inktBreedte?.() ?? 0),
      }),
      [opgeslagen],
    )

    async function kiesOpgeslagen() {
      setFout(null)
      setBezig(true)
      try {
        const url = await haalVoorbeeldUrl()
        if (!url) throw new Error('geen opgeslagen handtekening')
        const dataUrl = await urlNaarDataUrl(url)
        setOpgeslagen(dataUrl)
        padProps.onChange?.(false)
      } catch {
        setFout('Je opgeslagen handtekening kon niet worden geladen. Teken hieronder, of probeer het opnieuw.')
      } finally {
        setBezig(false)
      }
    }

    if (opgeslagen) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-center rounded-md border border-streep-border bg-white p-3">
            {/* eslint-disable-next-line @next/next/no-img-element -- data-URL van de eigen opgeslagen handtekening */}
            <img src={opgeslagen} alt="Je opgeslagen handtekening" className="max-h-24 max-w-full object-contain" />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-streep-text-muted">Je opgeslagen handtekening</span>
            <button
              type="button"
              onClick={() => {
                setOpgeslagen(null)
                padProps.onChange?.(true)
              }}
              className="min-h-11 px-2 text-sm font-medium text-streep-donker underline underline-offset-2"
            >
              Zelf tekenen
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-2">
        {heeftOpgeslagen && (
          <button
            type="button"
            disabled={bezig || padProps.disabled}
            onClick={kiesOpgeslagen}
            className="min-h-11 w-full rounded-md border border-streep-donker bg-white px-3 text-sm font-semibold text-streep-donker disabled:opacity-50"
          >
            {bezig ? 'Laden...' : 'Gebruik mijn opgeslagen handtekening'}
          </button>
        )}
        <SignaturePad ref={padRef} {...padProps} />
        {fout && (
          <p role="alert" className="text-sm text-red-700">
            {fout}
          </p>
        )}
      </div>
    )
  },
)
